import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Grid, FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { ArrowBack, PersonAdd } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { employeeService, staffService } from '@smartgrow/shared';
import { CurrencyInput } from '../../components/CurrencyInput';
import type { CreateEmployeeRequest, PaymentMethod } from '@smartgrow/shared/types';

const today = new Date().toISOString().split('T')[0];

const initial: CreateEmployeeRequest = {
  employeeNumber: undefined,
  fullName: '',
  email: '',
  phone: '',
  position: '',
  monthlySalary: 0,
  paymentDay: 25,
  paymentMethod: 'BANK_TRANSFER',
  bankAccountNumber: '',
  bankName: '',
  hireDate: today,
  userId: undefined,
  notes: '',
};

export const NewEmployeePage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateEmployeeRequest>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateEmployeeRequest, string>>>({});

  const { data: staffData } = useQuery({
    queryKey: ['staff-all'],
    queryFn: () => staffService.getStaff({ size: 200 }),
  });
  const staff = staffData?.content ?? [];

  const mutation = useMutation({
    mutationFn: (data: CreateEmployeeRequest) =>
      employeeService.createEmployee({
        ...data,
        employeeNumber: data.employeeNumber?.trim() || undefined,
        monthlySalary: Number(data.monthlySalary),
        paymentDay: Number(data.paymentDay),
        userId: data.userId || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        position: data.position || undefined,
        bankAccountNumber: data.bankAccountNumber || undefined,
        bankName: data.bankName || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      navigate('/dashboard/employees');
    },
  });

  const validate = () => {
    const e: typeof errors = {};
    if (form.employeeNumber && form.employeeNumber.length > 20) e.employeeNumber = 'Employee number must be 20 characters or less';
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.monthlySalary || Number(form.monthlySalary) <= 0) e.monthlySalary = 'Salary must be greater than 0';
    if (!form.hireDate) e.hireDate = 'Hire date is required';
    const day = Number(form.paymentDay);
    if (!day || day < 1 || day > 28) e.paymentDay = 'Payment day must be between 1 and 28';
    if (form.paymentMethod === 'BANK_TRANSFER' && !form.bankAccountNumber?.trim())
      e.bankAccountNumber = 'Bank account is required for bank transfer';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: keyof CreateEmployeeRequest, value: any) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="large">
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight="bold">Add Employee</Typography>
          <Typography variant="body2" color="text.secondary">Create a new employee payroll record</Typography>
        </Box>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to create employee. The employee number or email may already exist.
        </Alert>
      )}

      {/* Personal Information */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <PersonAdd color="primary" />
          <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Employee Number (auto-generated if blank)"
              value={form.employeeNumber}
              onChange={(e) => set('employeeNumber', e.target.value.toUpperCase())}
              fullWidth
              error={!!errors.employeeNumber}
              helperText={errors.employeeNumber || 'Leave blank to auto-generate (e.g. EMP-2026-0001)'}
              inputProps={{ maxLength: 20 }}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Full Name *"
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              fullWidth
              error={!!errors.fullName}
              helperText={errors.fullName}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Position / Role"
              value={form.position ?? ''}
              onChange={(e) => set('position', e.target.value)}
              fullWidth
              placeholder="e.g. Farm Supervisor"
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Hire Date *"
              type="date"
              value={form.hireDate}
              onChange={(e) => set('hireDate', e.target.value)}
              fullWidth
              error={!!errors.hireDate}
              helperText={errors.hireDate}
              InputLabelProps={{ shrink: true }}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email (optional)"
              type="email"
              value={form.email ?? ''}
              onChange={(e) => set('email', e.target.value)}
              fullWidth
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone (optional)"
              value={form.phone ?? ''}
              onChange={(e) => set('phone', e.target.value)}
              fullWidth
              size="medium"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Link to Staff Account (optional)"
              value={form.userId ?? ''}
              onChange={(e) => set('userId', e.target.value || undefined)}
              fullWidth
              helperText="Optionally link this employee record to a system user account"
              size="medium"
            >
              <MenuItem value="">None</MenuItem>
              {staff.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.fullName} (@{s.username})</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Salary & Payment */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Salary & Payment</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <CurrencyInput
              label="Monthly Salary *"
              value={form.monthlySalary || null}
              onChange={(v) => set('monthlySalary', v ?? 0)}
              fullWidth
              error={!!errors.monthlySalary}
              helperText={errors.monthlySalary}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Payment Day of Month *"
              type="number"
              value={form.paymentDay}
              onChange={(e) => set('paymentDay', Number.parseInt(e.target.value) || 25)}
              fullWidth
              error={!!errors.paymentDay}
              helperText={errors.paymentDay || 'Day 1–28 salary is processed each month'}
              inputProps={{ min: 1, max: 28 }}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Payment Method *"
              value={form.paymentMethod}
              onChange={(e) => set('paymentMethod', e.target.value as PaymentMethod)}
              fullWidth
              size="medium"
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </TextField>
          </Grid>

          {form.paymentMethod === 'BANK_TRANSFER' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Account Number *"
                  value={form.bankAccountNumber ?? ''}
                  onChange={(e) => set('bankAccountNumber', e.target.value)}
                  fullWidth
                  error={!!errors.bankAccountNumber}
                  helperText={errors.bankAccountNumber}
                  size="medium"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bank Name"
                  value={form.bankName ?? ''}
                  onChange={(e) => set('bankName', e.target.value)}
                  fullWidth
                  size="medium"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={(form as any).paymentEnabled !== false}
                    onChange={(e) => set('paymentEnabled' as any, e.target.checked)}
                  />
                }
                label="Include in Payroll"
              />
              <Tooltip title="When enabled, this employee will be included in monthly payroll runs and automatic salary payments. Disable for employees on leave or contract pause.">
                <Info fontSize="small" color="action" />
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes (optional)"
              multiline
              rows={3}
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth
              inputProps={{ maxLength: 500 }}
              size="medium"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4 }}>
        <Button
          size="large"
          onClick={() => navigate('/dashboard/employees')}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={() => validate() && mutation.mutate(form)}
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 160 }}
        >
          {mutation.isPending ? 'Creating...' : 'Add Employee'}
        </Button>
      </Box>
    </Box>
  );
};
